import React, { useState } from 'react';
import { Button, Modal, Form, Input, Table, Space, Tag, Typography, Row, Col, Select, InputNumber, Avatar, List, Pagination } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, CheckCircleOutlined, UserOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import apiService from '../../../services/api';
import { menteeProfileQueryKeys } from '../../../lib/queryClient';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';
import { MenteeSkillResponse, CreateMenteeSkillRequest, UpdateMenteeSkillRequest, SkillApprovalInfo } from '../../../types';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const SKILL_LEVELS = [
  { value: 'Beginner', label: 'Başlangıç', color: 'blue' },
  { value: 'Intermediate', label: 'Orta', color: 'green' },
  { value: 'Advanced', label: 'İleri', color: 'orange' },
  { value: 'Expert', label: 'Uzman', color: 'red' }
];

const SkillTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSkill, setEditingSkill] = useState<MenteeSkillResponse | null>(null);
  const [form] = Form.useForm();
  const [approvalsModalVisible, setApprovalsModalVisible] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<MenteeSkillResponse | null>(null);
  const [approvalsPage, setApprovalsPage] = useState(1);
  const [approvalsPageSize] = useState(10);

  const { data: skills = [], isLoading: loading } = useQuery({
    queryKey: menteeProfileQueryKeys.skills,
    queryFn: async () => {
      const res = await apiService.getMenteeSkills();
      if (!res.success) throw new Error(res.message || 'Yetkinlikler yüklenemedi.');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateMenteeSkillRequest) => apiService.createMenteeSkill(data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Yetkinlik başarıyla eklendi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.skills });
        setModalVisible(false);
        form.resetFields();
      } else toast.error(res.message || 'Yetkinlik eklenemedi.');
    },
    onError: () => toast.error('Yetkinlikler yüklenirken hata oluştu.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMenteeSkillRequest }) =>
      apiService.updateMenteeSkill(id, data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Yetkinlik başarıyla güncellendi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.skills });
        setModalVisible(false);
        setEditingSkill(null);
        form.resetFields();
      } else toast.error(res.message || 'Yetkinlik güncellenemedi.');
    },
    onError: () => toast.error('Yetkinlik güncellenirken hata oluştu.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteMenteeSkill(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Yetkinlik başarıyla silindi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.skills });
      } else toast.error(res.message || 'Yetkinlik silinemedi.');
    },
    onError: () => toast.error('Yetkinlik silinirken hata oluştu.'),
  });

  const handleAdd = () => {
    setEditingSkill(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (skill: MenteeSkillResponse) => {
    setEditingSkill(skill);
    form.setFieldsValue({
      skillName: skill.skillName,
      skillLevel: skill.skillLevel,
      experienceYears: skill.experienceYears,
      description: skill.description
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const skillData = {
        skillName: values.skillName,
        skillLevel: values.skillLevel,
        experienceYears: values.experienceYears,
        description: values.description
      };

      if (editingSkill) {
        updateMutation.mutate({ id: editingSkill.id, data: skillData });
      } else {
        createMutation.mutate(skillData);
      }
    } catch {
      // form validation
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingSkill(null);
    form.resetFields();
  };

  const handleDelete = (id: number) => deleteMutation.mutate(id);

  // Filtreleme
  const filteredSkills = skills.filter(skill =>
    skill.skillName.toLowerCase().includes(searchText.toLowerCase()) ||
    (skill.skillLevel && skill.skillLevel.toLowerCase().includes(searchText.toLowerCase())) ||
    (skill.description && skill.description.toLowerCase().includes(searchText.toLowerCase()))
  );

  const columns = [
    {
      title: 'Yetkinlik Adı',
      dataIndex: 'skillName',
      key: 'skillName',
      sorter: (a: MenteeSkillResponse, b: MenteeSkillResponse) => a.skillName.localeCompare(b.skillName),
    },
    {
      title: 'Seviye',
      dataIndex: 'skillLevel',
      key: 'skillLevel',
      render: (level: string) => {
        if (!level) return '-';
        const levelConfig = SKILL_LEVELS.find(l => l.value === level);
        return <Tag color={levelConfig?.color || 'default'}>{levelConfig?.label || level}</Tag>;
      },
      filters: SKILL_LEVELS.map(level => ({ text: level.label, value: level.value })),
      onFilter: (value: boolean | React.Key, record: MenteeSkillResponse) => record.skillLevel === value,
    },
    {
      title: 'Deneyim (Yıl)',
      dataIndex: 'experienceYears',
      key: 'experienceYears',
      render: (years: number) => years ? `${years} yıl` : '-',
      sorter: (a: MenteeSkillResponse, b: MenteeSkillResponse) => (a.experienceYears || 0) - (b.experienceYears || 0),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => description ? (description.length > 50 ? `${description.substring(0, 50)}...` : description) : '-',
    },
    {
      title: 'Onaylar',
      key: 'approvals',
      render: (_: any, record: MenteeSkillResponse) => {
        const approvalCount = record.approvalCount || 0;
        
        if (approvalCount === 0) {
          return <Tag color="default">Onay yok</Tag>;
        }

        return (
          <Tag 
            color="success" 
            icon={<CheckCircleOutlined />}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setSelectedSkill(record);
              setApprovalsModalVisible(true);
              setApprovalsPage(1);
            }}
          >
            {approvalCount} Onay
          </Tag>
        );
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MenteeSkillResponse) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: 'var(--text-secondary)' }}
          >
            Düzenle
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
    <PanelTabCard title="Yetkinliklerim" description="Yetkinliklerinizi ekleyin ve güncelleyin.">
        <TabListLoading loading={loading}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                backgroundColor: 'var(--text-secondary)',
                borderColor: 'var(--text-secondary)',
                fontWeight: 500
              }}
            >
              Yeni Yetkinlik
            </Button>
          </Col>
        </Row>
        
        <Row style={{ marginBottom: '16px' }}>
          <Col span={8}>
            <Input
              placeholder="Yetkinlik adı, seviye veya açıklama ara..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ borderRadius: '6px' }}
            />
          </Col>
        </Row>

        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={filteredSkills}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} yetkinlik`
          }}
        />
        </TabListLoading>
    </PanelTabCard>

      <Modal
        title={editingSkill ? 'Yetkinlik Düzenle' : 'Yeni Yetkinlik'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        okText={editingSkill ? 'Güncelle' : 'Ekle'}
        cancelText="İptal"
        okButtonProps={{
          style: { backgroundColor: 'var(--text-secondary)', borderColor: 'var(--text-secondary)' }
        }}
      >
        <Form layout="vertical" form={form} requiredMark={false}>
          <Form.Item 
            name="skillName" 
            label="Yetkinlik Adı" 
            rules={[{ required: true, message: 'Yetkinlik adı gerekli' }]}
          >
            <Input placeholder="Örn: JavaScript, React, C#" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="skillLevel" 
                label="Seviye"
              >
                <Select placeholder="Seviye seçin">
                  {SKILL_LEVELS.map(level => (
                    <Option key={level.value} value={level.value}>
                      {level.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="experienceYears" 
                label="Deneyim (Yıl)"
              >
                <InputNumber 
                  min={0} 
                  max={50} 
                  style={{ width: '100%' }} 
                  placeholder="0"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            name="description" 
            label="Açıklama"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="Bu yetkinlik hakkında ek bilgiler..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Onaylayan Mentörler Modal */}
      <Modal
        title={`${selectedSkill?.skillName} - Onaylayan Mentörler`}
        open={approvalsModalVisible}
        onCancel={() => {
          setApprovalsModalVisible(false);
          setSelectedSkill(null);
          setApprovalsPage(1);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setApprovalsModalVisible(false);
            setSelectedSkill(null);
            setApprovalsPage(1);
          }}>
            Kapat
          </Button>
        ]}
        width={600}
      >
        {selectedSkill && (
          <div>
            <div style={{ marginBottom: '16px', color: '#8c8c8c' }}>
              Toplam {selectedSkill.approvalCount || 0} mentör bu yetkinliği onayladı
            </div>
            {selectedSkill.approvals && selectedSkill.approvals.length > 0 ? (
              <>
                <List
                  dataSource={selectedSkill.approvals.slice(
                    (approvalsPage - 1) * approvalsPageSize,
                    approvalsPage * approvalsPageSize
                  )}
                  renderItem={(approval: SkillApprovalInfo) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} />}
                        title={
                          <div>
                            <Typography.Text strong>
                              {approval.mentorFirstName} {approval.mentorLastName}
                            </Typography.Text>
                            {approval.mentorEmail && (
                              <Typography.Text type="secondary" style={{ marginLeft: '8px', fontSize: '12px' }}>
                                {approval.mentorEmail}
                              </Typography.Text>
                            )}
                          </div>
                        }
                        description={
                          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                            {dayjs(approval.approvedAt).format('DD MMMM YYYY, HH:mm')}
                          </Typography.Text>
                        }
                      />
                    </List.Item>
                  )}
                />
                {selectedSkill.approvals.length > approvalsPageSize && (
                  <div style={{ marginTop: '16px', textAlign: 'center' }}>
                    <Pagination
                      current={approvalsPage}
                      pageSize={approvalsPageSize}
                      total={selectedSkill.approvals.length}
                      onChange={(page) => setApprovalsPage(page)}
                      showSizeChanger={false}
                      showQuickJumper
                      showTotal={(total, range) => `${range[0]}-${range[1]} / ${total} mentör`}
                    />
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px' }}>
                <Typography.Text type="secondary">Henüz onaylayan mentör bulunmuyor</Typography.Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default SkillTab;
